import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router } from '@angular/router';
import { AuthService, LoanSelectionService, LoggingService } from '@ld.consumer-portal/utils';

const EXTERNAL_ROUTE_MAP: Record<string, string> = {
  payment: '/payment',
  autopay: '/autopay',
  paymentsManageBankAccount: '/payment/manage-accounts',
  autopayManageBankAccount: '/payment/manage-accounts',
  editPayment: '/edit-payment',
  paymenthistory: '/dashboard',
};

export const externalAppLaunchGuard: CanActivateFn = (route: ActivatedRouteSnapshot) => {
  const loggingService = inject(LoggingService);
  const router = inject(Router);
  const authService = inject(AuthService);
  const loanSelectionService = inject(LoanSelectionService);

  if (!authService.isAuthenticated()) {
    return router.createUrlTree(['/login']);
  }

  const externalPath = route.paramMap.get('externalPath') || route.url[0]?.path;

  const encodedLoanNumber = route.queryParamMap.get('commonFieldsLoanNumber') || route.queryParamMap.get('ln');

  let loanNumber: string | null = null;
  if (encodedLoanNumber) {
    try {
      loanNumber = atob(encodedLoanNumber);
    } catch (error) {
      loggingService.logException(error, 'Failed to decode loan number from external link');
    }
  }

  if (loanNumber) {
    const currentLoan = loanSelectionService.selectedLoan();
    if (currentLoan?.loanNumber !== loanNumber) {
      loanSelectionService.selectLoanById(loanNumber);
    }
  }

  const internalRoute = EXTERNAL_ROUTE_MAP[externalPath || ''];

  if (internalRoute) {
    const queryParams: Record<string, string> = {};

    if (externalPath === 'paymenthistory') {
      queryParams['showPending'] = 'true';
    }

    if (loanNumber) {
      return router.createUrlTree([internalRoute, loanNumber], { queryParams });
    }

    return router.createUrlTree([internalRoute], { queryParams });
  }

  loggingService.logWarning(`Unknown external path: ${externalPath}, redirecting to dashboard`);
  return router.createUrlTree(['/dashboard']);
};

export const externalCallbackGuard: CanActivateFn = (route: ActivatedRouteSnapshot) => {
  const router = inject(Router);
  const authService = inject(AuthService);

  if (!authService.isAuthenticated()) {
    return router.createUrlTree(['/login']);
  }

  const status = route.queryParamMap.get('status');
  const confirmationNumber = route.queryParamMap.get('confirmationNumber');
  const errorCode = route.queryParamMap.get('errorCode');

  if (status === 'success' && confirmationNumber) {
    return router.createUrlTree(['/payment/confirmation'], {
      queryParams: { confirmationNumber },
    });
  }

  if (status === 'error' || errorCode) {
    return router.createUrlTree(['/payment/error'], {
      queryParams: { errorCode },
    });
  }

  return router.createUrlTree(['/dashboard']);
};
